import { useState, useEffect } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { addNewShift } from "../../redux/actions/shift";
import { getAmbulanceList } from "../../redux/actions/ambulances";
import { getAllAmbulanceEmployeeMapping } from "../../apis/ambulance";
import { Switch, DatePicker } from "antd";
import Card from "antd/es/card/Card";
import CustomButton from "../../common/CustomButton";
import CustomSelect from "../../common/CustomSelect";
import dayjs from "dayjs";

function AddShift() {
  const allAmbulances = useSelector((state) => state.ambulances.data);
  const ambulanceMappings = useSelector((state) => state.ambulanceEmployeeMapping?.data.object, shallowEqual);
  const dispatch = useDispatch();
  const [employeeList, setEmployeeList] = useState([]);
  const [shiftDetails, setShiftDetails] = useState({
    ambulanceId: '',
    shiftType: '',
    geofencing: true,
    employeeIds: undefined,
    fromDate: '',
    toDate: ''
  });

  const [selectedAmbulance, setSelectedAmbulance] = useState(null);
  const [shiftDuration, setShiftDuration] = useState("1 Day");

  const shiftOptions = [
    { value: 'SHIFT_8AMTO8PM', label: '8AM to 8PM Shift' },
    { value: 'SHIFT_8PMTO8AM', label: '8PM to 8AM Shift' },
    { value: 'SHIFT_8AMTO8AM', label: '8AM to 8AM Shift' }
  ];

  const durationOptions = [
    { value: "1 Day", label: "1 Day Shift" },
    { value: "1 Week", label: "1 Week Shift" },
    { value: "Custom", label: "Custom Range" }
  ];

  useEffect(() => {
    dispatch(getAmbulanceList());
  }, [dispatch]);

  function handleAddShift() {
    dispatch(addNewShift(shiftDetails));
  }

  function changeGeoFencing() {
    setShiftDetails({ ...shiftDetails, geofencing: !shiftDetails.geofencing });
  }

  function handleFromDateChange(date, dateString) {
    if (!date) return;

    if (shiftDuration === "1 Day") {
      setShiftDetails({ ...shiftDetails, fromDate: dateString, toDate: dateString });
    } else if (shiftDuration === "1 Week") {
      const toDateCalculated = dayjs(date).add(6, 'day').format("YYYY-MM-DD");
      setShiftDetails({ ...shiftDetails, fromDate: dateString, toDate: toDateCalculated });
    } else if (shiftDuration === "Custom") {
      setShiftDetails({ ...shiftDetails, fromDate: dateString, toDate: '' });
    }
  }

  function handleToDateChange(date, dateString) {
    setShiftDetails({ ...shiftDetails, toDate: dateString });
  }

  function handleDurationChange(value) {
    setShiftDuration(value);
    if (shiftDetails.fromDate) {
      const fromDateObj = dayjs(shiftDetails.fromDate);

      if (value === "1 Day") {
        setShiftDetails(prev => ({
          ...prev,
          toDate: prev.fromDate
        }));
      } else if (value === "1 Week") {
        const toDateCalculated = fromDateObj.add(6, 'day').format("YYYY-MM-DD");
        setShiftDetails(prev => ({
          ...prev,
          toDate: toDateCalculated
        }));
      } else if (value === "Custom") {
        setShiftDetails(prev => ({
          ...prev,
          toDate: ''
        }));
      }
    }
  }

  const employeeOptions = () =>
    employeeList.map(emp => ({
      value: emp.employeeId,
      label: emp.employeeName
    }));

  return (
    <Card 
      title="Create New Shift" 
      bordered 
      style={{ border: "2px solid rgb(0, 168, 204)", fontSize: '1.2rem' }}
      styles={{
        header: { borderBottom: "2px solid rgb(0, 168, 204)", fontSize: "2rem", textAlign: "center", color: "rgb(0, 168, 204)" }
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", justifyContent: "center" }}>
      <CustomSelect
        label="Select Ambulance"
        placeholder="Choose an ambulance"
        value={selectedAmbulance?.ambulanceId}
        options={allAmbulances?.map(amb => ({
          label: amb.ambulanceNumber,
          value: amb.ambulanceId
        }))}
        onChange={async (val) => {
          const selected = allAmbulances.find(item => item.ambulanceId === val);
          setSelectedAmbulance(selected);
          setShiftDetails(prev => ({ ...prev, ambulanceId: val }));
        
          try {
            const res = await getAllAmbulanceEmployeeMapping({ ambulanceNumber: selected.ambulanceNumber });
            const fetchedEmployees = res?.object?.[0]?.employeeList || [];
            setEmployeeList(fetchedEmployees);
            setShiftDetails(prev => ({
              ...prev,
              employeeIds: fetchedEmployees.map(emp => emp.employeeId)
            }));
          } catch (err) {
            console.error("Failed to fetch employees for ambulance:", err);
            setEmployeeList([]);
            setShiftDetails(prev => ({ ...prev, employeeIds: [] }));
          }
        }}
        showSearch
        optionFilterProp="label"
        type="default"
        style={{ minWidth: 220 }}
      />
        <CustomSelect
          label="Select the Shift"
          value={shiftDetails?.shiftType}
          options={shiftOptions}
          onChange={(val) => setShiftDetails({ ...shiftDetails, shiftType: val })}
          style={{ minWidth: 220 }}
        />
        <div>
          <label>From Date:</label><br />
          <DatePicker
            style={{ width: 220 }}
            value={shiftDetails.fromDate ? dayjs(shiftDetails.fromDate) : null}
            format="YYYY-MM-DD"
            onChange={handleFromDateChange}
          />
        </div>
        {shiftDetails.fromDate && 
          <CustomSelect
            label={"Shift Duration:"}
            value={shiftDuration}
            options={durationOptions}
            onChange={handleDurationChange}
            style={{ minWidth: 220 }}
          />
        }
        {shiftDuration === "Custom" && (
          <div>
            <label>To Date:</label><br />
            <DatePicker 
              style={{ width: 220 }}
              value={shiftDetails.toDate ? dayjs(shiftDetails.toDate) : null}
              format="YYYY-MM-DD"
              onChange={handleToDateChange}
              disabled={!shiftDetails.fromDate}
              disabledDate={(current) => current && current.isBefore(dayjs(shiftDetails.fromDate), 'day')}
            />
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <label>Geofencing:</label>
          <Switch checked={shiftDetails.geofencing} onChange={changeGeoFencing} />
        </div>

        <CustomSelect
          label="Select Employees"
          placeholder="Select employees"
          mode="multiple"
          value={shiftDetails.employeeIds}
          options={employeeOptions()}
          onChange={(selected) => setShiftDetails({ ...shiftDetails, employeeIds: selected })}
          style={{ minWidth: 220 }}
        />
      </div>

      <div style={{ display: "flex", width: "100%", justifyContent: "center", marginTop: "1rem" }}>
        <CustomButton type="primary" onClick={handleAddShift} title="Add Shift" />
      </div>
    </Card>
  );
}

export default AddShift;
